import { useCallback, useEffect, useState } from 'react';

import { TIME_PRESETS, formatHour } from '../../engine/index.js';
import { useEngineBus } from '../hooks/useEngineBus.js';
import Panel from '../components/Panel.jsx';
import Button from '../components/Button.jsx';
import Slider from '../components/Slider.jsx';

const SPEEDS = [1, 4, 16, 64];

/**
 * Cosmetic-only period word for the collapsed pill — derived client-side
 * from the snapshot's own curves, NOT part of the bus contract (the bus
 * `label` field is just `formatHour(hour)`, e.g. "13:45").
 */
function periodLabel(snap) {
  if (snap.night > 0.6) return 'night';
  if (snap.sunset > 0.35) return snap.hour < 12 ? 'dawn' : 'dusk';
  return 'day';
}

/**
 * Weather states, for the expanded panel's buttons. Ids must match
 * `engine/systems/Weather.js#WEATHER`; the glyph comes off the bus payload, so
 * this table only has to carry the ORDER (the chain, plus fog off to the side).
 */
const WEATHER_BUTTONS = ['clear', 'overcast', 'rain', 'storm', 'fog'];

/**
 * @file Collapsed by default (top-center pill) so it never collides with a
 * dev scene's own bottom-center plain-DOM day/night strip. Presence is
 * entirely driven by whether the engine's `'time'` channel has ever fired —
 * that is exactly "does `engine.dayNight` exist and is it bound to this
 * engine's bus", so there is no separate existence check to keep in sync.
 *
 * The WEATHER half rides the same rule on its own `'weather'` channel: a scene
 * with no `Weather` system never fires it, `wx` stays null, and the pill is
 * exactly the pill it always was. Nothing here reaches for `engine.weather` to
 * decide whether to render — an engine field can exist a frame before it has
 * anything to say.
 */
export default function TimeOfDay({ engine }) {
  const snap = useEngineBus(engine, 'time', { throttle: 150 });
  const wx = useEngineBus(engine, 'weather', { throttle: 250 });
  const [expanded, setExpanded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dragHour, setDragHour] = useState(null);

  // Safety net: if the mouse/touch is released outside the slider (a fast
  // drag off the element), the input itself never sees the up/end event —
  // a window-level listener still ends the drag so a stale `dragHour` can't
  // linger and block live snapshot updates forever.
  useEffect(() => {
    if (!dragging) return undefined;
    const end = () => {
      setDragging(false);
      setDragHour(null);
    };
    window.addEventListener('mouseup', end);
    window.addEventListener('touchend', end);
    return () => {
      window.removeEventListener('mouseup', end);
      window.removeEventListener('touchend', end);
    };
  }, [dragging]);

  const startDrag = useCallback(() => setDragging(true), []);
  const endDrag = useCallback(() => {
    setDragging(false);
    setDragHour(null);
  }, []);
  const onSlide = useCallback(
    (e) => {
      const v = Number(e.target.value);
      setDragHour(v);
      engine?.dayNight?.setHour(v);
    },
    [engine],
  );

  if (!snap) return null; // no DayNight bound to this engine (yet, or ever)

  const hour = dragging && dragHour != null ? dragHour : snap.hour;

  return (
    <div className="pointer-events-auto absolute left-1/2 top-3 -translate-x-1/2 select-none">
      {!expanded ? (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setExpanded(true)}
          className="rounded-full border border-hairline bg-void/80 px-3.5 py-1.5 font-hud text-[12px] font-bold uppercase tracking-[0.18em] text-hud backdrop-blur-sm transition hover:border-gold hover:text-gold"
        >
          {wx ? <span className="mr-1.5 tracking-normal">{wx.glyph}</span> : null}
          {periodLabel(snap)} · {formatHour(hour)}
        </button>
      ) : (
        <Panel className="w-72 p-3">
          <div className="flex items-center justify-between">
            <span className="font-hud text-[12px] font-bold uppercase tracking-[0.18em] text-hud">
              {wx ? <span className="mr-1.5 tracking-normal">{wx.glyph}</span> : null}
              {periodLabel(snap)} · {formatHour(hour)}
            </span>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setExpanded(false)}
              aria-label="collapse"
              className="text-hud-faint transition hover:text-hud"
            >
              ×
            </button>
          </div>

          <Slider
            className="mt-3"
            min={0}
            max={24}
            step={0.05}
            value={hour}
            onChange={onSlide}
            onDragStart={startDrag}
            onDragEnd={endDrag}
          />

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <Button active={snap.playing} onClick={() => engine?.dayNight?.togglePlay()}>
              {snap.playing ? 'pause' : 'play'}
            </Button>
            <div className="mx-0.5 h-4 w-px bg-[#22222e]" />
            {SPEEDS.map((s) => (
              <Button key={s} active={snap.speed === s} onClick={() => engine?.dayNight?.setSpeed(s)}>
                {s}×
              </Button>
            ))}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {TIME_PRESETS.map((p) => (
              <Button key={p.id} onClick={() => engine?.dayNight?.setHour(p.hour)}>
                {p.label}
              </Button>
            ))}
          </div>

          {/* Weather. Absent entirely when nothing has ever emitted `'weather'`,
              so a dev scene's panel is untouched. The buttons PIN the state —
              the schedule would otherwise roll the choice away inside a couple
              of minutes, which is useless for judging a look. */}
          {wx ? (
            <div className="mt-3 border-t border-hairline pt-2.5">
              <div className="mb-1.5 font-mono text-[10px] leading-4 text-hud-faint">
                weather: <span className="text-hud-dim">{wx.name}</span>
                {wx.pinned ? ' · pinned' : ''}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {WEATHER_BUTTONS.map((id) => (
                  <Button
                    key={id}
                    active={wx.state === id}
                    onClick={() => engine?.weather?.setState(id, { pin: true })}
                  >
                    {id}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}
        </Panel>
      )}
    </div>
  );
}
